/* eslint-disable @typescript-eslint/naming-convention */
import { isNull, isNumber } from 'lodash';
import { createCanvas } from '@/utils/file-kit';
import { type EdgelessCanvasService } from '..';
import { removeFromArray, type IEdgelessPlugin } from '../../../shared';
import { type EdgelessObject } from '../edgeless-object';

export class FocusPlugin implements IEdgelessPlugin {
    private __focusCanvas: HTMLCanvasElement | null = null;
    private __focusCtx: CanvasRenderingContext2D | null = null;

    objects: EdgelessObject[] = [];

    constructor(private __edgeless: EdgelessCanvasService) {}

    get count() {
        return this.objects.length;
    }

    exit(obj: EdgelessObject | null = null) {
        const layerIds: string[] = [];
        if (isNull(obj)) {
            for (const object of this.objects) {
                object.exitFocus();
                layerIds.push(object.layerId);
            }
            this.__setDirtyFrameIds(this.objects);
            this.objects = [];
        } else {
            if (this.objects.includes(obj)) {
                this.__setDirtyFrameIds([obj]);
                removeFromArray(this.objects, obj);
                obj.exitFocus();
                layerIds.push(obj.layerId);
            }
        }
        if (!this.count) {
            this.__focusCanvas = null;
            this.__focusCtx = null;
        }

        this.__edgeless.requestRenderAll();

        return layerIds;
    }

    enter(objects: EdgelessObject[]) {
        this.objects = objects;

        this.__setDirtyFrameIds(objects);
        this.__edgeless.requestRenderAll();
    }

    private __setDirtyFrameIds(objects: EdgelessObject[]) {
        objects.forEach((object) => {
            const frameId = this.__edgeless.service.frame.getLayerFrameId(object.layerId);
            if (isNumber(frameId)) {
                this.__edgeless.addDirtyFrameId(frameId);
            }
        });
    }

    render() {
        if (!this.count) {
            this.__focusCanvas = null;
            this.__focusCtx = null;
            return;
        }
        const { highlightArea, maskRgba } = this.objects[0]?.focusInfo || {};
        const edgelessCtx = this.__edgeless.getContext();
        // 普通高亮模式
        if (highlightArea) {
            for (const object of this.objects) {
                if (object.shouldSkipFocusRender()) continue;
                this.__edgeless.renderObject(edgelessCtx, object);
            }
            if (maskRgba) {
                if (!this.__focusCanvas) {
                    const { canvas, ctx } = createCanvas(this.__edgeless.canvas.width, this.__edgeless.canvas.height);
                    this.__focusCanvas = canvas;
                    this.__focusCtx = ctx;
                }
                this.__focusCanvas.width = this.__edgeless.canvas.width;
                this.__focusCanvas.height = this.__edgeless.canvas.height;
                if (this.__focusCtx) {
                    const ctx = this.__focusCtx;
                    const { zoom, translateX, translateY } = this.__edgeless.renderZoomAndTranslate;
                    ctx.clearRect(0, 0, this.__focusCanvas.width, this.__focusCanvas.height);
                    ctx.save();
                    ctx.fillStyle = maskRgba;
                    ctx.fillRect(0, 0, this.__focusCanvas.width, this.__focusCanvas.height);
                    ctx.translate(highlightArea.x * zoom + translateX, highlightArea.y * zoom + translateY);
                    ctx.clearRect(0, 0, highlightArea.width * zoom, highlightArea.height * zoom);
                    ctx.scale(zoom, zoom);
                    ctx.restore();
                    edgelessCtx.drawImage(this.__focusCanvas, 0, 0);
                }
            }
        } else {
            if (maskRgba) {
                this.__edgeless.fillContext(maskRgba);
            }
            for (const object of this.objects) {
                if (object.shouldSkipFocusRender()) continue;
                this.__edgeless.renderObject(edgelessCtx, object);
            }
        }
    }
}
